/**
 * Brand Controller
 * Handles all brand-related API calls and data transformations
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Add this helper function to get auth headers
const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    Authorization: authToken ? `Bearer ${authToken}` : ''
  };
};

const BrandController = {
  /**
   * Fetch brands with optional filtering and pagination
   * @param {number} pageNumber - Current page number
   * @param {number} size - Items per page
   * @param {string} searchTerm - Optional search filter
   * @returns {Promise<Object>} - Paginated brand data
   */
  fetchBrands: async (pageNumber = 1, size = 10, searchTerm = '') => {
    try {
      let url = `${API_URL}/Marque/Pagination?Size=${size}&PageNumber=${pageNumber}`;
      
      // Add search parameter if provided
      if (searchTerm) {
        url += `&Search=${encodeURIComponent(searchTerm)}`;
      }
      
      // Only change: Add headers to the request
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
    
  },

  fetchAllBrands: async (searchTerm = '') => {
    try {
      let url = `${API_URL}/Marque`;
      
      // Add search parameter if provided
      if (searchTerm) {
        url += `&Search=${encodeURIComponent(searchTerm)}`;
      }
      
      // Only change: Add headers to the request
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  },
  
  /**
   * Create a new brand
   * @param {Object} brandData - Contains name and other brand properties
   * @returns {Promise<Object>} - Created brand data
   */
  createBrand: async (brandData) => {
    try {
      const formData = new FormData();
      formData.append('Name', brandData.name ?? '');
      if (brandData.pixelId) {
        formData.append('PixelId', brandData.pixelId);
      }
      if (brandData.token) {
        formData.append('Token', brandData.token);
      }
      if (brandData.image) {
        formData.append('Image', brandData.image);
      }

      const response = await axios.post(`${API_URL}/Marque`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating brand:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing brand
   * @param {Object} brandData - Contains id and name of brand to update
   * @returns {Promise<Object>} - Updated brand data
   */
  updateBrand: async (brandData) => {
    try {
      const formData = new FormData();
      formData.append('Id', brandData.id);
      formData.append('Name', brandData.name ?? '');
      if (brandData.pixelId) {
        formData.append('PixelId', brandData.pixelId);
      }
      if (brandData.token) {
        formData.append('Token', brandData.token);
      }
      if (brandData.imagePath) {
        formData.append('ImagePath', brandData.imagePath);
      }
      if (brandData.image) {
        formData.append('Image', brandData.image);
      }

      const response = await axios.put(`${API_URL}/marque`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating brand #${brandData.id}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete a brand
   * @param {number} brandId - ID of brand to delete
   * @returns {Promise<boolean>} - Success indicator
   */
  deleteBrand: async (brandId) => {
    try {
      // Only change: Add headers to the request
      await axios.delete(`${API_URL}/Marque/${brandId}`, { headers: getAuthHeaders() });
      return true;
    } catch (error) {
      console.error(`Error deleting brand #${brandId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get a single brand by ID
   * @param {number} brandId - ID of brand to fetch
   * @returns {Promise<Object>} - Brand data
   */
  getBrandById: async (brandId) => {
    try {
      // Only change: Add headers to the request
      const response = await axios.get(`${API_URL}/Marque/${brandId}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching brand #${brandId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get a single brand by ID (new endpoint)
   * @param {number} id - ID of brand to fetch
   * @returns {Promise<Object>} - Brand data
   */
  getBrandById: async (id) => {
    try {
      // Only change: Add headers to the request
      const response = await axios.get(`${API_URL}/Marque/ById/${id}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching brand #${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Get all brands (without pagination)
   * @returns {Promise<Array>} - All brands
   */
  getAllBrands: async () => {
    try {
      // Only change: Add headers to the request
      const response = await axios.get(`${API_URL}/Marque`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error('Error fetching all brands:', error);
      throw error;
    }
  }
};

export default BrandController;